/**
 * getAssessmentModuleCardWidthClass
 * ---------------------------------
 * Returns the responsive Tailwind width classes for the OUTER wrapper of each
 * assessment-module card in the Patient Overview "Appointments"/"Assessment
 * Modules" row (shared by the ADHD overview, the Autism overview, and their
 * loading skeleton).
 *
 * Why this exists (business context for future maintainers):
 *   The overview row is a single-line `inline-flex` with `justify-between`, so
 *   the per-card wrapper width has to add up to roughly the full row width or
 *   the spare space gets spread out as large gaps between cards. Historically
 *   every org showed exactly FOUR module cards, so the wrapper width was
 *   hardcoded to `xl:w-[24%]` (4 x 24% ≈ 96%, a full-looking row).
 *
 *   Lancaster Medical Practice runs the Autism pathway WITHOUT a Behavioural
 *   Observation appointment, so its overview shows only THREE cards. With the
 *   old fixed 24% width, three cards filled just 72% at the `xl` breakpoint and
 *   `justify-between` opened up big empty bands between them. This helper makes
 *   the width depend on the card count so a 3-card row fills the space evenly.
 *
 * Design decisions:
 *   - Only the `xl` (>=1280px) segment changes with the count. At `lg` a 3-card
 *     row already fills ~96% (3 x 32%), and the smaller breakpoints (`w-1/2`,
 *     `sm:w-[48%]`) intentionally wrap to two-per-row, so they are unaffected.
 *   - Any count other than 3 (i.e. the standard 4, or any unexpected value)
 *     keeps the legacy `xl:w-[24%]`. This guarantees the 4-card ADHD/TOC layout
 *     is byte-for-byte identical to before and makes the helper safe by default.
 *   - We deliberately do NOT use `flex-1`: it would widen the existing 4-up
 *     cards and collapse the `justify-between` gaps, changing the TOC/ADHD look.
 *
 * @param {number} moduleCount - Number of visible module cards in the row.
 * @returns {string} Space-separated Tailwind class string for the card wrapper.
 */
export function getAssessmentModuleCardWidthClass(moduleCount) {
  // 3-up (Autism without Observation) needs ~32% each to fill the xl row the
  // same way 4-up does at 24%; everything else keeps the legacy 4-up width.
  const xlWidth = moduleCount === 3 ? 'xl:w-[32%]' : 'xl:w-[24%]';
  return `w-1/2 sm:w-[48%] lg:w-[32%] ${xlWidth}`;
}

export default getAssessmentModuleCardWidthClass;
